import React, { useEffect, useState } from "react";
import {
  AlertTriangle,
  Eye,
  Loader2,
  Search,
  Square,
  SquareCheckBig,
  Wallet,
  X,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { toast } from "react-toastify";
import api from "~/services/api";
import { formatter } from "~/functions";
import ModalShell from "../../Gravity Conciliation/Preview/ModalShell";
import CreateEntryTab from "./CreateEntryTab";
import PaymentMethodModal from "./PaymentMethodModal";
import {
  theme,
  field,
  primaryButton,
  ghostButton,
  label,
  spread as row,
} from "../../Gravity Conciliation/Preview/modalStyles";

function safeDate(value) {
  if (!value) return "-";
  try {
    return format(parseISO(value), "MM/dd/yyyy");
  } catch (err) {
    return "-";
  }
}

function settlementName(candidate, side) {
  if (side === "Payeesettlement") {
    return (
      candidate.payee?.issuer?.merchant?.name ||
      candidate.merchant?.name ||
      candidate.name ||
      "-"
    );
  }
  const student =
    candidate.receivable?.issuer?.student || candidate.student || null;
  if (student) {
    return `${student.name || ""} ${student.last_name || ""}`.trim() || "-";
  }
  return candidate.name || "-";
}

function settlementInvoice(candidate) {
  return (
    candidate.invoice_number ||
    candidate.receivable?.invoice_number ||
    candidate.payee?.invoice_number ||
    "-"
  );
}

function batchName(candidate) {
  if (candidate.batch_number) return `Gravity batch #${candidate.batch_number}`;
  if (candidate.memo) return `Other batch - ${candidate.memo}`;
  return candidate.type ? `Other batch - ${candidate.type}` : "Batch";
}

function batchAmount(candidate) {
  return Number(candidate.amount ?? candidate.total_amount ?? 0);
}

function batchDate(candidate) {
  return candidate.date || candidate.conciliation_date || candidate.date_to;
}

function alreadyUsedLabel(alreadyUsed) {
  if (!alreadyUsed) return "";
  const when = alreadyUsed.date ? safeDate(alreadyUsed.date) : null;
  const status = alreadyUsed.status ? ` - ${alreadyUsed.status}` : "";
  if (alreadyUsed.conciliation_type === "GravityConciliation") {
    return `Already reconciled in Gravity batch #${
      alreadyUsed.batch_number ?? "-"
    }${when ? ` of ${when}` : ""}${status}`;
  }
  const description = alreadyUsed.memo || alreadyUsed.type || "";
  return `Already reconciled in Other batch${
    description ? ` - ${description}` : ""
  }${when ? ` of ${when}` : ""}${status}`;
}

function recordLink(candidate, side) {
  if (side === "Payeesettlement") {
    const payeeId = candidate.payee?.id || candidate.payee_id;
    return payeeId ? `/Financial/Payees/Payees?id=${payeeId}` : null;
  }
  const receivableId =
    candidate.receivable?.id || candidate.receivable_id || null;
  return receivableId
    ? `/Financial/Receivables/Receivables?id=${receivableId}`
    : null;
}

export default function MatchesModal({
  item,
  bankAccountId,
  onClose,
  onConciliated,
}) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("byDate");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [memo, setMemo] = useState("");
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [paymentMethodOpen, setPaymentMethodOpen] = useState(false);

  async function loadCandidates(
    searchValue = null,
    paymentMethodValue = paymentMethod
  ) {
    setLoading(true);
    try {
      const params = {};
      if (searchValue) params.search = searchValue;
      if (paymentMethodValue?.id) params.paymentmethod_id = paymentMethodValue.id;

      const { data: result } = await api.get(
        `/bankconciliations-items/${item.id}/candidates`,
        Object.keys(params).length ? { params } : {}
      );
      setData(result);

      if (!searchValue && !paymentMethodValue?.id) {
        const resultItem = result?.item || item;
        if (
          Number(resultItem.amount || 0) === 0 &&
          resultItem.status !== "Verified" &&
          resultItem.status !== "OnlyBank"
        ) {
          setTab("onlyBank");
          return;
        }
        const nothingFound =
          !(result?.byDate || []).length &&
          !(result?.byAmount || []).length &&
          !(result?.byName || []).length &&
          !(result?.gravity_conciliations || []).length &&
          !(result?.other_conciliations || []).length;
        if (nothingFound) {
          setTab("createEntry");
        }
      }
    } catch (err) {
      toast(err?.response?.data?.error || "Could not load candidates.", {
        type: "error",
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadCandidates();
  }, [item.id]);

  const side = data?.side || (item.type === "Credit" ? "Settlement" : "Payeesettlement");
  const currentItem = data?.item || item;
  const isDebit = currentItem.type === "Debit";
  const isZeroAmount = Number(currentItem.amount || 0) === 0;
  const canOnlyBank =
    isZeroAmount &&
    currentItem.status !== "Verified" &&
    currentItem.status !== "OnlyBank";

  const batches = [
    ...(data?.gravity_conciliations || []).map((candidate) => ({
      ...candidate,
      __type: "GravityConciliation",
    })),
    ...(data?.other_conciliations || []).map((candidate) => ({
      ...candidate,
      __type: "OtherConciliation",
    })),
  ];

  const tabs = [
    { key: "byDate", title: "By date", rows: data?.byDate || [] },
    { key: "byAmount", title: "By amount", rows: data?.byAmount || [] },
    { key: "bySearch", title: "Search", rows: data?.bySearch || [] },
    ...((data?.byName || []).length
      ? [{ key: "byName", title: "By name", rows: data.byName }]
      : []),
    ...(batches.length ? [{ key: "batches", title: "Batches", rows: batches }] : []),
    canOnlyBank
      ? { key: "onlyBank", title: "Mark as Only Bank", rows: [] }
      : { key: "createEntry", title: "+ Create entry", rows: [] },
  ];

  const activeTab = tabs.find((definition) => definition.key === tab) || tabs[0];
  const rows = activeTab?.rows || [];
  const isBatchTab = tab === "batches";
  const isCreateTab = tab === "createEntry";
  const isOnlyBankTab = tab === "onlyBank";

  function handleSearch(event) {
    event.preventDefault();
    setTab("bySearch");
    setSelected(null);
    loadCandidates(search);
  }

  function handleSelectPaymentMethod(method) {
    setPaymentMethod(method);
    setPaymentMethodOpen(false);
    setSelected(null);
    loadCandidates(tab === "bySearch" ? search : null, method);
  }

  function describeSelected() {
    if (!selected) return "-";
    if (selected.__type) return batchName(selected);
    return settlementName(selected, side);
  }

  function resolveType() {
    if (selected?.__type) return selected.__type;
    return side;
  }

  async function handleOnlyBank() {
    if (!memo.trim()) {
      toast("A memo is required to mark this movement as Only Bank.", {
        type: "warning",
        autoClose: 3000,
      });
      return;
    }
    setSaving(true);
    try {
      await api.post(`/bankconciliations-items/${item.id}/only-bank`, {
        memo: memo.trim(),
      });
      toast("Movement marked as Only Bank!", { autoClose: 1500 });
      onConciliated();
      onClose();
    } catch (err) {
      toast(err?.response?.data?.error || "Could not mark as Only Bank.", {
        type: "error",
        autoClose: 5000,
      });
    } finally {
      setSaving(false);
    }
  }

  async function handleConfirm() {
    if (isOnlyBankTab) {
      await handleOnlyBank();
      return;
    }
    if (!selected) {
      toast("Select one option.", { type: "warning", autoClose: 2000 });
      return;
    }
    if (selected.already_used) {
      toast(alreadyUsedLabel(selected.already_used), {
        type: "warning",
        autoClose: 4000,
      });
      return;
    }
    setSaving(true);
    try {
      await api.post(`/bankconciliations-items/${item.id}/link`, {
        conciliation_type: resolveType(),
        target_id: selected.id,
        ...(memo.trim() ? { memo: memo.trim() } : {}),
      });
      toast("Movement reconciled!", { autoClose: 1000 });
      onConciliated();
      onClose();
    } catch (err) {
      const usedElsewhere = err?.response?.data?.already_used;
      toast(
        usedElsewhere
          ? alreadyUsedLabel(usedElsewhere)
          : err?.response?.data?.error || "Could not reconcile.",
        { type: "error", autoClose: usedElsewhere ? 5000 : 3000 }
      );
      if (usedElsewhere) {
        setSelected(null);
        loadCandidates(tab === "bySearch" ? search : null);
      }
    } finally {
      setSaving(false);
    }
  }

  const isCredit = currentItem.type === "Credit";

  return (
    <ModalShell title="Reconcile movement" onClose={onClose} draggable>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: 16,
          borderBottom: `1px solid ${theme.border}`,
          color: theme.text,
          flexShrink: 0,
          flexWrap: "wrap",
        }}
      >
        <strong style={{ color: isCredit ? theme.success : theme.danger }}>
          {isCredit ? "IN" : "OUT"}
        </strong>
        <span>·</span>
        <span>{safeDate(currentItem.date)}</span>
        <span>·</span>
        <span>{currentItem.description || "-"}</span>
        <span>·</span>
        <strong>{formatter.format(Number(currentItem.amount || 0))}</strong>
        <span style={{ ...label, marginLeft: "auto" }}>
          Looking for: {side === "Payeesettlement" ? "Payables" : "Receivables"}
        </span>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "16px 16px 0 16px",
          flexShrink: 0,
          flexWrap: "wrap",
        }}
      >
        {tabs.map((definition) => (
          <button
            key={definition.key}
            type="button"
            onClick={() => {
              setTab(definition.key);
              setSelected(null);
            }}
            style={{
              ...(tab === definition.key ? primaryButton : ghostButton),
              padding: "5px 14px",
            }}
          >
            {definition.title}
            {definition.rows.length ? ` (${definition.rows.length})` : ""}
          </button>
        ))}

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            marginLeft: "auto",
          }}
        >
          <button
            type="button"
            onClick={() => setPaymentMethodOpen(true)}
            title="Filter by payment method"
            style={{
              ...(paymentMethod ? primaryButton : ghostButton),
              padding: "5px 14px",
              maxWidth: 220,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <Wallet size={14} />
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {paymentMethod?.description || "Payment method"}
            </span>
          </button>
          {paymentMethod ? (
            <button
              type="button"
              onClick={() => handleSelectPaymentMethod(null)}
              title="Clear payment method filter"
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: theme.muted,
                display: "flex",
              }}
            >
              <X size={14} />
            </button>
          ) : null}
        </div>
      </div>

      {tab === "bySearch" ? (
        <form
          onSubmit={handleSearch}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "12px 16px 0 16px",
            flexShrink: 0,
          }}
        >
          <input
            type="text"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Invoice number..."
            style={{ ...field, flex: 1, width: "auto" }}
          />
          <button type="submit" style={primaryButton}>
            <Search size={14} /> Search
          </button>
        </form>
      ) : null}

      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: 16 }}>
        {isOnlyBankTab ? (
          <div style={{ color: theme.text, lineHeight: 1.6 }}>
            <p style={{ marginBottom: 12 }}>
              This movement has a <strong>zero amount</strong>, so there is no
              receivable or payable to match it with.
            </p>
            <p style={{ marginBottom: 12 }}>
              Marking it as <strong>Only Bank</strong> keeps it on the bank
              statement without creating any receivable, payable or settlement.
              The statement can be closed normally with it.
            </p>
            <p style={{ color: theme.muted }}>
              A memo is required to explain why this movement was disregarded.
              You can undo this later from the statement.
            </p>
          </div>
        ) : isCreateTab ? (
          <CreateEntryTab
            item={currentItem}
            isDebit={isDebit}
            bankAccountId={bankAccountId}
            onCancel={onClose}
            onCreated={() => {
              onConciliated();
              onClose();
            }}
          />
        ) : loading ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              padding: "32px 0",
              color: theme.muted,
            }}
          >
            <Loader2 size={16} className="animate-spin" /> Loading...
          </div>
        ) : rows.length === 0 ? (
          <div
            style={{ textAlign: "center", color: theme.muted, padding: "32px 0" }}
          >
            No candidates in this tab.
          </div>
        ) : (
          <table
            style={{
              width: "100%",
              border: `1px solid ${theme.border}`,
              borderRadius: 6,
              borderCollapse: "collapse",
            }}
          >
            <tbody>
              {rows.map((candidate) => {
                const checked = selected?.id === candidate.id;
                const alreadyUsed = !isBatchTab
                  ? candidate.already_used || null
                  : null;
                const blocked = !!alreadyUsed;
                return (
                  <tr
                    key={`${candidate.__type || tab}-${candidate.id}`}
                    onClick={() => {
                      if (blocked) return;
                      setSelected(candidate);
                    }}
                    style={{
                      cursor: blocked ? "not-allowed" : "pointer",
                      borderBottom: `1px solid ${theme.border}`,
                      backgroundColor: checked ? theme.successBg : "transparent",
                      opacity: blocked ? 0.55 : 1,
                      color: blocked ? theme.muted : undefined,
                    }}
                  >
                    <td style={{ padding: 8, width: 32 }}>
                      {blocked ? (
                        <span className="relative inline-block group align-text-top z-10">
                          <AlertTriangle
                            size={16}
                            className="inline-block text-orange-600"
                            style={{ cursor: "help" }}
                          />
                          <span className="pointer-events-none absolute left-0 top-full mt-1 hidden group-hover:block whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-xs text-white shadow-lg z-50">
                            {alreadyUsedLabel(alreadyUsed)}
                          </span>
                        </span>
                      ) : checked ? (
                        <SquareCheckBig size={16} color={theme.success} />
                      ) : (
                        <Square size={16} color={theme.border} />
                      )}
                    </td>
                    <td style={{ padding: 8 }}>
                      {isBatchTab
                        ? batchName(candidate)
                        : settlementName(candidate, side)}
                    </td>
                    <td style={{ padding: 8, color: theme.muted }}>
                      {isBatchTab ? "" : settlementInvoice(candidate)}
                    </td>
                    <td style={{ padding: 8, color: theme.muted }}>
                      {safeDate(
                        isBatchTab
                          ? batchDate(candidate)
                          : candidate.settlement_date || candidate.date
                      )}
                    </td>
                    <td
                      style={{
                        padding: 8,
                        textAlign: "right",
                        fontWeight: "bold",
                      }}
                    >
                      {formatter.format(
                        isBatchTab
                          ? batchAmount(candidate)
                          : Number(candidate.amount || 0)
                      )}
                    </td>
                    <td style={{ padding: 8, width: 32 }}>
                      {!isBatchTab && recordLink(candidate, side) ? (
                        <button
                          type="button"
                          title="Open record in a new tab"
                          onClick={(event) => {
                            event.stopPropagation();
                            window.open(
                              recordLink(candidate, side),
                              "_blank",
                              "noopener"
                            );
                          }}
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: theme.muted,
                            display: "flex",
                          }}
                        >
                          <Eye size={14} />
                        </button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div
        style={{
          borderTop: `1px solid ${theme.border}`,
          backgroundColor: theme.subtle,
          padding: 16,
          display: isCreateTab ? "none" : "flex",
          flexDirection: "column",
          gap: 8,
          flexShrink: 0,
        }}
      >
        <div style={row}>
          <span style={label}>Movement</span>
          <strong>{formatter.format(Number(currentItem.amount || 0))}</strong>
        </div>
        <div style={row}>
          <span style={label}>Selected</span>
          <strong>{isOnlyBankTab ? "Only Bank" : describeSelected()}</strong>
        </div>

        <textarea
          rows={2}
          value={memo}
          onChange={(event) => setMemo(event.target.value)}
          placeholder={isOnlyBankTab ? "Memo (required)" : "Memo (optional)"}
          style={field}
        />

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 8,
            paddingTop: 8,
          }}
        >
          <button type="button" onClick={onClose} style={ghostButton}>
            Cancel
          </button>
          <button
            type="button"
            disabled={saving || (isOnlyBankTab && !memo.trim())}
            onClick={handleConfirm}
            style={{
              ...primaryButton,
              opacity: saving || (isOnlyBankTab && !memo.trim()) ? 0.5 : 1,
            }}
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : null}
            {isOnlyBankTab ? "Mark as Only Bank" : "Confirm"}
          </button>
        </div>
      </div>

      {paymentMethodOpen ? (
        <PaymentMethodModal
          selectedId={paymentMethod?.id || null}
          onSelect={handleSelectPaymentMethod}
          onClose={() => setPaymentMethodOpen(false)}
        />
      ) : null}
    </ModalShell>
  );
}
